import type { ThemeConfig } from "antd";
import { theme } from "antd";

/**
 * Centralized color palette — single source of truth.
 * Indigo-violet primary with teal accent for a modern SaaS feel.
 */
export const colors = {
  // Primary palette
  primary: "#6366f1", // Indigo 500
  primaryLight: "#818cf8", // Indigo 400
  primaryDark: "#4f46e5", // Indigo 600
  primaryBg: "#eef2ff", // Indigo 50

  // Secondary
  secondary: "#8b5cf6", // Violet 500
  secondaryLight: "#a78bfa", // Violet 400
  secondaryDark: "#7c3aed", // Violet 600

  // Accent / Success
  accent: "#14b8a6", // Teal 500
  accentLight: "#2dd4bf", // Teal 400
  accentDark: "#0d9488", // Teal 600

  // Semantic
  warning: "#f59e0b", // Amber 500
  error: "#ef4444", // Red 500
  info: "#3b82f6", // Blue 500

  // Dark mode neutrals (Slate)
  darkBg: "#0f172a", // Slate 900
  darkCard: "#1e293b", // Slate 800
  darkBorder: "#334155", // Slate 700
  darkText: "#e2e8f0", // Slate 200
} as const;

/**
 * Reusable gradient strings.
 */
export const gradients = {
  primary: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
  loginLight: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 50%, ${colors.secondaryLight} 100%)`,
  loginDark: `linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, ${colors.darkCard} 100%)`,
} as const;

/**
 * Shared token overrides applied to both light and dark themes.
 */
const sharedTokens: ThemeConfig["token"] = {
  colorPrimary: colors.primary,
  colorSuccess: colors.accent,
  colorWarning: colors.warning,
  colorError: colors.error,
  colorInfo: colors.info,
  // 25% larger input/select/textarea
  controlHeight: 40,
  controlHeightLG: 50,
  controlHeightSM: 30,
  // Typography
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
  // Refined border radius
  borderRadius: 8,
  borderRadiusLG: 12,
  borderRadiusSM: 6,
};

/**
 * Light theme configuration.
 */
export const lightTheme: ThemeConfig = {
  algorithm: [theme.defaultAlgorithm],
  token: {
    ...sharedTokens,
  },
};

/**
 * Dark theme configuration with intentional Slate-based neutrals.
 */
export const darkTheme: ThemeConfig = {
  algorithm: [theme.darkAlgorithm],
  token: {
    ...sharedTokens,
    colorBgContainer: colors.darkCard,
    colorBgElevated: "#263348",
    colorBgLayout: colors.darkBg,
    colorBorderSecondary: colors.darkBorder,
  },
  components: {
    Menu: {
      itemSelectedColor: "#e0e7ff", // Indigo 100 — high contrast on dark bg
      itemSelectedBg: "rgba(99, 102, 241, 0.15)", // Subtle indigo tint
    },
    Input: {
      colorBgContainer: "#1a2744", // Darker than card bg for visible input fields
    },
  },
};
